import { action, makeObservable, observable, reaction } from "mobx";
import React from "react";
import { AcxStore } from "stores/RootStore";
import type { IRootStore } from "stores/RootStore";
import { DrawerStore } from "../../components/UI/Drawer/AcxDrawer";
import { BaseStore } from "../BaseStore";

export const defaultDrawerOffset = 72; // default to 72 because that is the height of the current top appBar

@AcxStore
export class LayoutDrawerStore extends BaseStore implements DrawerStore {
    @observable isOpen: boolean = false;
    @observable anchor: "left" | "right" | "top" | "bottom" = "right";
    @observable variant: "permanent" | "persistent" | "temporary" = "permanent";
    @observable collapseSize: "mini" | "full" = "full";
    @observable canOpen = false;
    @observable offsetPx?: number = defaultDrawerOffset;
    @observable offsetPosition?: "Left" | "Top" | "Right" | "Bottom" = "Top";
    @observable customRootStyles?: React.CSSProperties;
    @observable transitionDuration?: number;
    @observable width?: number;
    @observable footerButtons: React.ReactNode;

    @observable.ref contentInjector?: () => React.ReactNode;

    constructor(private rootStore: IRootStore) {
        super("FocusPageDrawerStore");

        makeObservable(this);

        reaction(
            (r) => this.contentInjector,
            (arg) => {
                this.canOpen = Boolean(arg);
                if (!this.canOpen) {
                    this.setOpen(false);
                }
            },
            { fireImmediately: true },
        );
    }

    @action
    setContentFactory(factory?: () => React.ReactNode) {
        this.contentInjector = factory;
    }

    @action
    setFooterButtons(input: React.ReactNode) {
        this.footerButtons = input;
    }

    @action
    setOffsetPixels(px?: number) {
        this.offsetPx = px;
    }

    @action
    setOffsetPosition(position?: "Left" | "Top" | "Right" | "Bottom") {
        this.offsetPosition = position;
    }

    @action
    setCustomRootStyles(styles: React.CSSProperties) {
        this.customRootStyles = styles;
    }

    @action
    setVariant(variant: "permanent" | "persistent" | "temporary") {
        this.variant = variant;
    }

    @action
    setCollapseSize(size: "mini" | "full") {
        this.collapseSize = size;
    }
    @action
    setAnchor(anchor: "left" | "right" | "top" | "bottom") {
        this.anchor = anchor;
    }

    @action
    setTransitionDuration(duration?: number) {
        this.transitionDuration = duration;
    }

    @action
    setOpen(isOpen: boolean) {
        this.isOpen = isOpen;
    }

    @action
    setWidth(input: number) {
        this.width = input;
    }

    @action
    reset = (keepContent?: boolean) => {
        this.setOpen(false);
        if (!keepContent) {
            this.contentInjector = undefined;
            this.footerButtons = undefined;
        }
    };

    @action
    closeDrawer = () => {
        this.isOpen = false;
    };

    @action
    closeAndResetDrawer = (keepContent?: boolean) => {
        this.closeDrawer();
        this.reset(keepContent);
    };

    @action
    openDrawer = () => {
        this.isOpen = Boolean(this.contentInjector);
    };

    @action
    restoreDefaults() {
        this.anchor = "right";
        this.variant = "permanent";
        this.collapseSize = "full";
        this.offsetPx = 72;
        this.offsetPosition = "Top";
        this.customRootStyles = undefined;
        // not sure we want to reset this to undefined, maybe a default instead
        this.transitionDuration = undefined;
        this.footerButtons = undefined;
    }
}
